import { Box, Card, IconButton, Tooltip, Typography, Stack, Table, TableRow, useTheme, Chip, Button } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { Download, Payment } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import {
    usePagination,
    useSortBy,
    useTable,
} from 'react-table';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import {
    BodyTableCell,
    HeadTableCell,
    StyledTableRow,
} from 'components/table/StyledTable';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import FormatDate from 'utils/formatDate';
import useBoolean from 'hooks/useBoolean';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useNavigate } from 'react-router-dom';

const MyInvoice = () => {
    const [dataInvoice, setDataInvoice] = useState([]);
    const theme = useTheme();
    const isLoading = useBoolean();
    const navigate = useNavigate();

    const columns = useMemo(
        () => [
            {
                Header: 'Created Date',
                accessor: 'createdAt',
                Cell: ({ value }) => {
                    const date = FormatDate(value, 'dd MMM yyyy');
                    const time = FormatDate(value, 'HH:mm');
                    return (
                        <Box sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{date}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {time}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                Header: 'Invoice Info',
                accessor: 'number',
                Cell: ({ row }) => (
                    <>
                        <Typography variant="body2">
                            {row.original.number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {row.original.package?.name || ''}
                        </Typography>
                    </>
                ),
            },
            {
                Header: 'Workshops',
                accessor: 'workshops',
                Cell: ({ value }) => {
                    const names = Array.isArray(value)
                        ? value.map((w) => w.name).filter(Boolean)
                        : [];
                    return (
                        <Typography variant="body2">
                            {names.length ? names.join(', ') : '-'}
                        </Typography>
                    );
                },
            },
            {
                Header: 'Total',
                accessor: 'totalPrice',
                Cell: ({ value }) => formatCurrency(value),
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }) => (
                    <Chip
                        label={value?.label || ''}
                        sx={{
                            backgroundColor: value?.bgColor || 'grey.200',
                            color: value?.color || 'text.primary',
                            fontWeight: 600,
                        }}
                        size="small"
                    />
                ),
            },
            {
                Header: 'Action',
                accessor: 'id',
                Cell: ({ row }) => (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton
                            onClick={() => handleDownload(row.original)}
                            disabled={isLoading.value}
                        >
                            <Tooltip title="Download Invoice">
                                <Download
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: 18,
                                    }}
                                />
                            </Tooltip>
                        </IconButton>
                        {row.original?.status?.value === 'pending' && (
                            <IconButton
                                onClick={() => handleCreatePayment(row.original)}
                                disabled={isLoading.value}
                            >
                                <Tooltip title="Create Payment">
                                    <Payment
                                        sx={{
                                            color: 'primary.main',
                                            fontSize: 18,
                                        }}
                                    />
                                </Tooltip>
                            </IconButton>
                        )}
                    </Box>
                ),
            },
    ],
        [isLoading.value],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageOptions,
        gotoPage,
    } = useTable(
        { columns, data: dataInvoice },
        useSortBy,
        usePagination,
    );

    const handleDownload = async (data) => {
        try {
            isLoading.onTrue();
            await downloadFile(`/api/prv/member-invoices/download/${data.id}`);
        } finally {
            isLoading.onFalse();
        }
    };

    const handleCreatePayment = (data) => {
        const type = data?.package?.type;
        if (type === 'main') {
            navigate('/dashboard/choose-package');
        } else if (type === 'additional') {
            navigate('/dashboard/choose-additional-workshop');
        }
    };

    const getData = async () => {
        try {
            isLoading.onTrue();
            const res = await axiosInstance.get(`/api/prv/member-invoices`, {
                params: {
                    packageTypes: ['main', 'additional'],
                    statuses: ['paid', 'pending'],
                },
            });
            setDataInvoice(res.data || []);
        } finally {
            isLoading.onFalse();
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const handleChange = (_, currentPageNo) => gotoPage(currentPageNo - 1);

    return (
        <DashboardLayout>
            <Box pt={2} pb={4}>
                {Array.isArray(dataInvoice) && dataInvoice.length === 0 ? (
                    <Card
                        sx={{
                            py: 6,
                            px: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} mb={1}>
                            No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            You don’t have any invoice yet. Choose a package or add a workshop to create one.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <Button
                                variant="contained"
                                onClick={() => navigate('/dashboard/choose-package')}
                            >
                                Buy Package
                            </Button>
                        </Stack>
                    </Card>
                ) : (
                    <Card sx={{ py: 2 }}>
                        <Scrollbar autoHide={true}>
                            <Table
                                {...getTableProps()}
                                sx={{
                                    minWidth: 900,
                                }}
                            >
                                <TableHead
                                    sx={{
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {headerGroups.map((headerGroup, index) => (
                                        <TableRow
                                            key={'header-row' + index}
                                            {...headerGroup.getHeaderGroupProps()}
                                        >
                                            {headerGroup.headers.map(
                                                (column, indexCell) => (
                                                    <HeadTableCell
                                                        key={'header-cell' + indexCell}
                                                        {...column.getHeaderProps(
                                                            column.getSortByToggleProps(),
                                                        )}
                                                        sx={{
                                                            '&:last-child': {
                                                                textAlign: 'center',
                                                            },
                                                        }}
                                                    >
                                                        {column.render('Header')}
                                                    </HeadTableCell>
                                                ),
                                            )}
                                        </TableRow>
                                    ))}
                                </TableHead>

                                <TableBody {...getTableBodyProps()}>
                                    {page.map((row, indexRow) => {
                                        prepareRow(row);
                                        return (
                                            <StyledTableRow
                                                {...row.getRowProps()}
                                                key={'row' + indexRow}
                                            >
                                                {row.cells.map((cell, indexCell) => {
                                                    return (
                                                        <BodyTableCell
                                                            key={'column' + indexCell}
                                                            {...cell.getCellProps()}
                                                        >
                                                            {cell.render('Cell')}
                                                        </BodyTableCell>
                                                    );
                                                })}
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Scrollbar>

                        <Stack alignItems="center" marginY={4}>
                            <AppPagination
                                shape="rounded"
                                onChange={handleChange}
                                count={pageOptions.length}
                            />
                        </Stack>
                    </Card>
                )}
            </Box>
        </DashboardLayout>
    );
};

export default MyInvoice;
